import React, { useState, useMemo, useCallback } from 'react';
import { Quote, SortConfig } from '../types';
import { ArrowUpDown, Heart, Search, X, ChevronLeft, ChevronRight, Trash2, Plus, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import QuoteModal from './QuoteModal';
import ExportButton from './ExportButton';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import QuoteActionsDropdown from './QuoteActionsDropdown';
import { useNavigate } from 'react-router-dom';

interface QuotesTableProps {
  quotes: Quote[];
  onUpdateQuote: (id: string, updates: Partial<Quote>) => Promise<{ success: boolean; error?: string }>;
  onDeleteQuotes: (ids: string[]) => Promise<{ success: boolean; error?: string }>;
}

const QUOTES_PER_PAGE = 10;
const FILTER_STORAGE_KEY = 'quoteFilters';

const loadFilterPreferences = () => {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading filter preferences:', error);
  }
  return { showFavorites: false, showQuotewall: false };
};

export default function QuotesTable({ quotes, onUpdateQuote, onDeleteQuotes }: QuotesTableProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'year', direction: 'desc' });
  const [activeQuote, setActiveQuote] = useState<Quote | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [filterPreferences, setFilterPreferences] = useState(loadFilterPreferences());
  const [selectedQuotes, setSelectedQuotes] = useState<Set<string>>(new Set());

  const { showFavorites, showQuotewall } = filterPreferences;

  console.log('Initial state:', {
    quotes: quotes.length,
    searchQuery,
    sortConfig,
    currentPage,
    filterPreferences
  });

  const filteredAndSortedQuotes = useMemo(() => {
    console.log('Filtering and sorting quotes:', {
      totalQuotes: quotes.length,
      searchQuery,
      showFavorites,
      showQuotewall,
      sortConfig
    });

    const filtered = quotes.filter(quote => {
      const query = searchQuery.toLowerCase();
      const matchesAuthor = quote.author.toLowerCase().includes(query);
      const matchesYear = (quote.year?.toString() || '').includes(query);
      const matchesFilters = (!showFavorites && !showQuotewall) ||
                            (showFavorites && quote.isFavorite) ||
                            (showQuotewall && quote.quotewall);
      return (matchesAuthor || matchesYear) && matchesFilters;
    });

    console.log('After filtering:', {
      filteredCount: filtered.length,
      favoriteCount: filtered.filter(q => q.isFavorite).length,
      quotewallCount: filtered.filter(q => q.quotewall).length
    });

    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Handle numerical fields (age, year) with proper null handling
      if (sortConfig.key === 'age' || sortConfig.key === 'year') {
        const aNum = aValue ?? (sortConfig.direction === 'asc' ? Infinity : -Infinity);
        const bNum = bValue ?? (sortConfig.direction === 'asc' ? Infinity : -Infinity);
        
        return sortConfig.direction === 'asc'
          ? (aNum as number) - (bNum as number)
          : (bNum as number) - (aNum as number);
      }
      
      return sortConfig.direction === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    console.log('After sorting:', {
      sortedCount: sorted.length,
      firstItem: sorted[0],
      lastItem: sorted[sorted.length - 1]
    });

    return sorted;
  }, [quotes, searchQuery, showFavorites, showQuotewall, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedQuotes.length / QUOTES_PER_PAGE);
  const startIndex = (currentPage - 1) * QUOTES_PER_PAGE;
  const endIndex = Math.min(startIndex + QUOTES_PER_PAGE, filteredAndSortedQuotes.length);
  const paginatedQuotes = filteredAndSortedQuotes.slice(startIndex, endIndex);

  console.log('Pagination calculation:', {
    totalQuotes: filteredAndSortedQuotes.length,
    totalPages,
    currentPage,
    startIndex,
    endIndex,
    paginatedQuotesCount: paginatedQuotes.length
  });

  // Ensure current page is valid after filtering
  React.useEffect(() => {
    const maxPage = Math.max(1, totalPages);
    if (currentPage > maxPage) {
      console.log('Adjusting current page:', {
        currentPage,
        maxPage,
        reason: 'Current page exceeds total pages'
      });
      setCurrentPage(maxPage);
    }
  }, [filteredAndSortedQuotes.length, totalPages, currentPage]);

  const handleQuoteUpdate = async (id: string, updates: Partial<Quote>) => {
    const result = await onUpdateQuote(id, updates);
    if (result.success) {
      setActiveQuote(null);
    }
    return result;
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      const result = await onDeleteQuotes(Array.from(selectedQuotes));
      if (result.success) {
        setSelectedQuotes(new Set());
        setDeleteSuccess(true);
        setTimeout(() => setDeleteSuccess(false), 3000);
      } else {
        setDeleteError(result.error || 'Failed to delete quotes');
      }
    } catch (err) {
      setDeleteError('An unexpected error occurred');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-gray-900">Your Quotes</h1>
        <button
          onClick={() => navigate('/add')}
          className="btn-primary w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Quote
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by author or year..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="input pl-10"
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showFavorites}
                onChange={(e) => {
                  setFilterPreferences(prev => ({
                    ...prev,
                    showFavorites: e.target.checked
                  }));
                  setCurrentPage(1); // Reset to first page on filter change
                }}
                className="rounded text-brand-600 focus:ring-brand-500"
              />
              <span>Favorites</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showQuotewall}
                onChange={(e) => {
                  setFilterPreferences(prev => ({
                    ...prev,
                    showQuotewall: e.target.checked
                  }));
                  setCurrentPage(1); // Reset to first page on filter change
                }}
                className="rounded text-brand-600 focus:ring-brand-500"
              />
              <span>Quotewall</span>
            </label>
          </div>
        </div>
      </div>

      {/* Export and Delete Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <ExportButton
          selectedQuotes={paginatedQuotes.filter(q => selectedQuotes.has(q.id))}
          allQuotes={filteredAndSortedQuotes}
          onClearSelection={() => setSelectedQuotes(new Set())}
          onAddToQuotewall={async (ids) => {
            for (const id of ids) {
              await onUpdateQuote(id, { quotewall: true });
            }
          }}
        />
        
        {selectedQuotes.size > 0 && (
          <button
            onClick={() => setShowDeleteModal(true)}
            className="btn bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 w-full sm:w-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Selected ({selectedQuotes.size})
          </button>
        )}
      </div>

      {/* Mobile Quote Cards */}
      <div className="block lg:hidden space-y-4">
        {paginatedQuotes.map((quote) => (
          <motion.div
            key={quote.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="card p-4 space-y-4"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <input
                  type="checkbox"
                  checked={selectedQuotes.has(quote.id)}
                  onChange={() => {
                    setSelectedQuotes(prev => {
                      const next = new Set(prev);
                      if (next.has(quote.id)) {
                        next.delete(quote.id);
                      } else {
                        next.add(quote.id);
                      }
                      return next;
                    });
                  }}
                  className="rounded text-brand-600 focus:ring-brand-500 h-5 w-5"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div 
                  className="text-base text-gray-900 break-words cursor-pointer"
                  onClick={() => setActiveQuote(quote)}
                >
                  "{quote.text}"
                </div>
                <div className="mt-2 text-sm font-medium text-gray-700">
                  — {quote.author}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm text-gray-600">
                  {quote.age && (
                    <div>
                      <span className="font-medium">Age:</span> {quote.age || '—'}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Year:</span> {quote.year || '—'}
                  </div>
                  {quote.location && (
                    <div className="col-span-2">
                      <span className="font-medium">Location:</span> {quote.location}
                    </div>
                  )}
                  {quote.context && (
                    <div className="col-span-2">
                      <span className="font-medium">Context:</span> {quote.context}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleQuoteUpdate(quote.id, { isFavorite: !quote.isFavorite })}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Heart
                    className={`h-6 w-6 ${
                      quote.isFavorite ? 'fill-red-500 text-red-500' : ''
                    }`}
                  />
                </button>
                <QuoteActionsDropdown
                  quote={quote}
                  onRemoveFromQuotewall={() => handleQuoteUpdate(quote.id, { quotewall: false })}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="w-[50px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={paginatedQuotes.length > 0 && paginatedQuotes.every(q => selectedQuotes.has(q.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedQuotes(new Set(paginatedQuotes.map(q => q.id)));
                      } else {
                        setSelectedQuotes(new Set());
                      }
                    }}
                    className="rounded text-brand-600 focus:ring-brand-500"
                  />
                </th>
                <th scope="col" className="w-[70px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Favorite
                </th>
                {[
                  { key: 'text', label: 'Quote' },
                  { key: 'author', label: 'Author' },
                  { key: 'age', label: 'Age' },
                  { key: 'location', label: 'Location' },
                  { key: 'context', label: 'Context' },
                  { key: 'year', label: 'Year' }
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    scope="col"
                    onClick={() => setSortConfig(prev => ({
                      key: key as keyof Quote,
                      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
                    }))}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
                  >
                    <div className="flex items-center gap-2">
                      <span>{label}</span>
                      <ArrowUpDown className={`h-4 w-4 transition-opacity ${
                        sortConfig.key === key ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`} />
                    </div>
                  </th>
                ))}
                <th scope="col" className="w-[80px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedQuotes.map((quote) => (
                <motion.tr
                  key={quote.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedQuotes.has(quote.id)}
                      onChange={() => {
                        setSelectedQuotes(prev => {
                          const next = new Set(prev);
                          if (next.has(quote.id)) {
                            next.delete(quote.id);
                          } else {
                            next.add(quote.id);
                          }
                          return next;
                        });
                      }}
                      className="rounded text-brand-600 focus:ring-brand-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleQuoteUpdate(quote.id, { isFavorite: !quote.isFavorite })}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Heart
                        className={`h-5 w-5 ${
                          quote.isFavorite ? 'fill-red-500 text-red-500' : ''
                        }`}
                      />
                    </button>
                  </td>
                  <td 
                    className="px-6 py-4 cursor-pointer hover:text-brand-600"
                    onClick={() => setActiveQuote(quote)}
                  >
                    <div className="line-clamp-2">{quote.text}</div>
                  </td>
                  <td className="px-6 py-4">{quote.author}</td>
                  <td className="px-6 py-4">{quote.age || '—'}</td>
                  <td className="px-6 py-4">{quote.location || '—'}</td>
                  <td className="px-6 py-4">{quote.context || '—'}</td>
                  <td className="px-6 py-4">{quote.year || '—'}</td>
                  <td className="px-6 py-4">
                    <QuoteActionsDropdown
                      quote={quote}
                      onRemoveFromQuotewall={() => handleQuoteUpdate(quote.id, { quotewall: false })}
                    />
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {endIndex} of{' '}
                {filteredAndSortedQuotes.length} quotes
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary p-2 disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`btn ${
                      currentPage === page
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary p-2 disabled:opacity-50"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredAndSortedQuotes.length === 0 && (
        <div className="text-center py-12 card">
          <p className="text-gray-500 text-lg">No quotes found</p>
        </div>
      )}

      {/* Modals */}
      {activeQuote && (
        <QuoteModal
          quote={activeQuote}
          onClose={() => setActiveQuote(null)}
          onUpdate={handleQuoteUpdate}
        />
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        selectedCount={selectedQuotes.size}
        isDeleting={isDeleting}
      />
    </div>
  );
}